import { useEffect, useState } from "react";
import { useLoading } from "../../../context/LoadingContext";
import {
  Users,
  AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getFamilles } from "../../../features/familles/api/familleAPI";
import FamilleCard from "../../../features/familles/components/FamilleCard";
import FamillesHeader from "../../../features/familles/components/FamillesHeader";
import SkeletonCard from "../../../components/ui/SkeletonCard";
import PaginationFooter from "../../../components/ui/PaginationFooter";
import { getPaginationState } from "../../../utils/pagination";

const PAGE_SIZE = 6;

export default function Families() {
  const [families, setFamilies] = useState([]);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const { isLoading: isPageLoading } = useLoading();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setLoading(true);
      const dataFamilles = await getFamilles();
      setFamilies(dataFamilles);
    } catch (err) {
      console.error("Erreur lors de la récupération des données :", err);
      setError("Impossible de charger les données.");
    } finally {
      setLoading(false);
    }
  }; 

  useEffect(() => {
    fetchData();
  }, []);

  // --- Filtrage & Pagination ---
  const famillesFiltrees = families.filter((f) => {
    const matchesSearch = `${f.nom} ${f.prenom || ""}`.toLowerCase().includes(search.toLowerCase());
    if (!matchesSearch) return false;
    
    if (activeFilter === "audit_requis") return !f.dateAudit;
    if (activeFilter === "audit_ok") return !!f.dateAudit;
    return true;
  });
  
  const { safePage, startIndex, endIndex } = getPaginationState({
    page: currentPage,
    pageSize: PAGE_SIZE,
    totalItems: famillesFiltrees.length,
  });
  const currentEntites = famillesFiltrees.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, activeFilter]);

  useEffect(() => {
    setCurrentPage((page) => getPaginationState({
      page,
      pageSize: PAGE_SIZE,
      totalItems: famillesFiltrees.length,
    }).safePage);
  }, [famillesFiltrees.length]);

  // Le loader global garde la main sur la transition de route.
  // Les blocs se montent ensuite ; la requete continue en parallele.
  if (isPageLoading) return null;

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-workspace p-4 sm:p-8">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={loading ? { opacity: 0, y: -10 } : { opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <FamillesHeader 
            onAddProspect={() => navigate("/prospects")} 
            search={search}
            setSearch={setSearch}
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
        />
      </motion.div>

      {/* Data Grid */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="flex-1 min-h-0 workspace-card overflow-hidden flex flex-col shadow-hp-sm"
      >
        <div className="flex-1 overflow-y-auto lg:overflow-hidden p-4 relative">
            {loading ? (
                <SkeletonCard count={6} />
              ) : error ? (
                <div className="flex-1 flex flex-col items-center justify-center text-danger-500 py-20">
                  <AlertCircle className="w-10 h-10 mb-2" />
                  {error}
                </div>
              ) : famillesFiltrees.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-subtle py-20">
                  <Users className="w-12 h-12 mx-auto mb-3 text-workspace" />
                  {families.length === 0
                    ? "Aucune famille n'est actuellement gérée."
                    : "Aucune famille ne correspond à ces critères."}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-slide-up h-full lg:grid-rows-2">
                  {currentEntites.map((family) => (
                    <FamilleCard
                      key={family.id}
                      family={family}
                      onClick={() => navigate(`/prospects/familles/${family.id}`)}
                    />
                  ))}
                </div>
              )}
        </div>

        {!loading && !error && famillesFiltrees.length > 0 && (
          <PaginationFooter
            page={safePage}
            pageSize={PAGE_SIZE}
            totalItems={famillesFiltrees.length}
            onPageChange={setCurrentPage}
            ariaLabel="Pagination des familles"
          />
        )}
      </motion.div>
    </div>
  );
}
